import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatestWith, switchMap, tap, withLatestFrom, catchError, map } from 'rxjs/operators';
import { EMPTY, firstValueFrom, from, of } from 'rxjs';
import * as fromActions from './actions';
import { Store } from '@ngrx/store';
import {
  NotificationService,
  PagedResultOfAlarmPushNotificationDto,
  PagedResultOfMaintenancePushNotificationDto,
  PushNotificationOrganizationService,
  PushNotificationService,
} from '@services/api/notification-api.service';
import { loadedOrganizationsSubscriber, selectSelectedOrganizationId, selectUser } from '@redux/index';
import { selectNotificationsAlarm, selectNotificationsMaintenance } from '@redux/modules/notifications/index';
import { OrganizationVehicleService } from '@services/api/api.service';
import { UserRole } from '@models/user';

@Injectable()
export class NotificationsEffects {
  private language = 'en';
  private isCustomer = false;

  setNotificationsAlarmFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setNotificationsAlarmFilters),
    tap(() => {
      this.store.dispatch(fromActions.setNotificationsAlarmPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  public getNotificationsAlarmList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.getNotificationsAlarmList,
        fromActions.setNotificationsAlarmPaginator),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      withLatestFrom(
        this.store.select(selectNotificationsAlarm),
      ),
      tap(() => this.store.dispatch(fromActions.setNotificationsAlarmLoading({ isLoading: true }))),
      switchMap(async ([[_, selectedOrganizationId], alarms]) => {
        let alarmsData: PagedResultOfAlarmPushNotificationDto;
        if (this.isCustomer) {
          alarmsData = await firstValueFrom(this.pushNotificationOrganizationService
            .getAlarmNotifications(
              selectedOrganizationId,
              alarms.filters.qSearch,
              alarms.filters.brand,
              alarms.filters.model,
              alarms.filters.startDate,
              alarms.filters.endDate,
              this.language,
              alarms.paginator.page,
              alarms.paginator.pageSize,
            ));
        } else {
          alarmsData = await firstValueFrom(this.pushNotificationService.getAlarmNotifications(
            alarms.filters.startDate,
            alarms.filters.endDate,
            this.language,
            alarms.paginator.page,
            alarms.paginator.pageSize,
          ));
        }
        return [
          fromActions.setNotificationsAlarmList({ response: alarmsData }),
          fromActions.setNotificationsAlarmLoading({ isLoading: false }),
        ];
      }),
      catchError(() => of([
        fromActions.setNotificationsAlarmList({ response: { items: [], total: 0 } }),
        fromActions.setNotificationsAlarmLoading({ isLoading: false }),
      ])),
      switchMap((actions) => [...actions]),
    );
  });

  setNotificationsMaintenanceFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setNotificationsMaintenanceFilters),
    tap(() => {
      this.store.dispatch(fromActions.setNotificationsMaintenancePaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  public getNotificationsMaintenanceList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.getNotificationsMaintenanceList,
        fromActions.setNotificationsMaintenancePaginator),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      withLatestFrom(
        this.store.select(selectNotificationsMaintenance),
      ),
      tap(() => this.store.dispatch(fromActions.setNotificationsMaintenanceLoading({ isLoading: true }))),
      switchMap(async ([[_, selectedOrganizationId], maintenances]) => {
        let maintenancesData: PagedResultOfMaintenancePushNotificationDto;
        if (this.isCustomer) {
          maintenancesData = await firstValueFrom(this.pushNotificationOrganizationService
            .getMaintenancesNotifications(
              selectedOrganizationId,
              maintenances.filters.qSearch,
              maintenances.filters.brand,
              maintenances.filters.model,
              maintenances.filters.startDate,
              maintenances.filters.endDate,
              this.language,
              maintenances.paginator.page,
              maintenances.paginator.pageSize,
            ));
        } else {
          maintenancesData = await firstValueFrom(this.pushNotificationService.getMaintenanceNotifications(
            maintenances.filters.startDate,
            maintenances.filters.endDate,
            this.language,
            maintenances.paginator.page,
            maintenances.paginator.pageSize,
          ));
        }
        return [
          fromActions.setNotificationsMaintenanceList({ response: maintenancesData }),
          fromActions.setNotificationsMaintenanceLoading({ isLoading: false }),
        ];
      }),
      catchError(() => of([
        fromActions.setNotificationsMaintenanceList({ response: { items: [], total: 0 } }),
        fromActions.setNotificationsMaintenanceLoading({ isLoading: false }),
      ])),
      switchMap((actions) => [...actions]),
    );
  });

  public getCustomerNotificationsBrands$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.getCustomerNotificationsBrands),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      switchMap(async ([_, selectedOrganizationId]) => {
        const brands = await firstValueFrom(this.organizationVehicleService
          .getVehicleBrands(
            selectedOrganizationId,
          ));
        return [
          fromActions.setCustomerNotificationsBrands({ brands }),
        ];
      }),
      catchError(() => of([
        fromActions.setCustomerNotificationsBrands({ brands: [] }),
      ])),
      switchMap((actions) => [...actions]),
    );
  });

  public getCustomerNotificationsModels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.getCustomerNotificationsModels),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      switchMap(async ([_, selectedOrganizationId]) => {
        const models = await firstValueFrom(this.organizationVehicleService
          .getVehicleModels(
            selectedOrganizationId,
          ));
        return [
          fromActions.setCustomerNotificationsModels({ models }),
        ];
      }),
      catchError(() => of([
        fromActions.setCustomerNotificationsModels({ models: [] }),
      ])),
      switchMap((actions) => [...actions]),
    );
  });

  getNotificationsCounter$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.getNotificationsCounter),
      switchMap(async (_) => {
        let counter: { unreadCountAlarm: number; unreadCountMaintenance: number; };
        if (this.isCustomer) {
          const alarmsCount = await firstValueFrom(this.pushNotificationOrganizationService.notReadAlarmsCounter());
          const unreadCountAlarm = alarmsCount.notRead;
          const maintenanceCount = await firstValueFrom(this.pushNotificationOrganizationService.notReadMaintenanceCounter());
          const unreadCountMaintenance = maintenanceCount.notRead;
          counter = { unreadCountAlarm, unreadCountMaintenance };
        } else {
          const alarmsCount = await firstValueFrom(this.pushNotificationService.notReadAlarmsCounter());
          const unreadCountAlarm = alarmsCount.notRead;
          const maintenanceCount = await firstValueFrom(this.pushNotificationService.notReadMaintenanceCounter());
          const unreadCountMaintenance = maintenanceCount.notRead;
          counter = { unreadCountAlarm, unreadCountMaintenance };
        }
        return [fromActions.setNotificationsCounter(counter)];
      }),
      catchError(() => of([fromActions.setNotificationsCounter({ unreadCountAlarm: 0, unreadCountMaintenance: 0 })])),
      switchMap((actions) => [...actions]));
  });

  markNotificationsAlarmAsRead$ = createEffect(() => {
    return this.actions$.pipe(ofType(fromActions.markNotificationsAlarmAsRead),
      switchMap(async (action) => {
        await firstValueFrom(this.pushNotificationService.markAsRead(action.id));
        return [fromActions.getNotificationsCounter()];
      }),
      catchError(() => of([fromActions.getNotificationsCounter()])),
      switchMap((actions) => [...actions]));
  });

  markNotificationsMaintenanceAsRead$ = createEffect(() => {
    return this.actions$.pipe(ofType(fromActions.markNotificationsMaintenanceAsRead),
      switchMap(async (action) => {
        await firstValueFrom(this.pushNotificationService.markAsRead(action.id));
        return [fromActions.getNotificationsCounter()];
      }),
      catchError(() => of([fromActions.getNotificationsCounter()])),
      switchMap((actions) => [...actions]));
  });

  markAllNotificationsAlarmAsRead$ = createEffect(() => {
    return this.actions$.pipe(ofType(fromActions.markAllNotificationsAlarmAsRead),
      switchMap(async () => {
        await firstValueFrom(this.pushNotificationOrganizationService.markAllAlarmsAsRead());
        return [fromActions.getNotificationsCounter(), fromActions.getNotificationsAlarmList()];
      }),
      catchError(() => of([fromActions.getNotificationsCounter(), fromActions.getNotificationsAlarmList()])),
      switchMap((actions) => [...actions]));
  });

  markAllNotificationsMaintenanceAsRead$ = createEffect(() => {
    return this.actions$.pipe(ofType(fromActions.markAllNotificationsMaintenanceAsRead),
      switchMap(async () => {
        await firstValueFrom(this.pushNotificationOrganizationService.markAllMaintenanceAsRead());
        return [fromActions.getNotificationsCounter(), fromActions.getNotificationsMaintenanceList()];
      }),
      catchError(() => of([fromActions.getNotificationsCounter(), fromActions.getNotificationsMaintenanceList()])),
      switchMap((actions) => [...actions]));
  });

  deleteNotificationsAlarm$ = createEffect(() => {
    return this.actions$.pipe(ofType(fromActions.deleteNotificationsAlarm),
      switchMap(async (action) => {
        await firstValueFrom(this.pushNotificationService.delete(action.id));
        return [fromActions.getNotificationsCounter(), fromActions.getNotificationsAlarmList()];
      }),
      catchError(() => of([fromActions.getNotificationsCounter(), fromActions.getNotificationsAlarmList()])),
      switchMap((actions) => [...actions]));
  });

  deleteNotificationsMaintenance$ = createEffect(() => {
    return this.actions$.pipe(ofType(fromActions.deleteNotificationsMaintenance),
      switchMap(async (action) => {
        await firstValueFrom(this.pushNotificationService.delete(action.id));
        return [fromActions.getNotificationsCounter(), fromActions.getNotificationsMaintenanceList()];
      }),
      catchError(() => of([fromActions.getNotificationsCounter(), fromActions.getNotificationsMaintenanceList()])),
      switchMap((actions) => [...actions]));
  });

  getNotificationsSettings$ = createEffect(() => {
    return this.actions$.pipe(ofType(fromActions.getNotificationsSettings),
      switchMap(async () => {
        try {
          const settings = await firstValueFrom(this.notificationService.getUserNotificationPreferences());
          return fromActions.setNotificationsSettings({ settings });
        } catch {
          return fromActions.setNotificationsSettings({ settings: null });
        }
      }));
  });

  updateNotificationsSettings$ = createEffect(() => {
    return this.actions$.pipe(ofType(fromActions.updateNotificationsSettings),
      switchMap((action) =>
        from(firstValueFrom(this.notificationService.updateUserNotificationPreferences(action.settings))).pipe(
          map(() => fromActions.setNotificationsSettings({ settings: action.settings })),
          catchError(() => EMPTY),
        )));
  });

  // public $obtainNotificationsJwt = createEffect(() => this.actions$.pipe(
  //   ofType(fromActions.obtainNotificationsJwt),
  //   tap(async () => {
  //     const accessToken = await firstValueFrom(this.auth.getAccessTokenSilently(),
  //     );
  //     this.hubConnection = new HubConnectionBuilder()
  //       .withUrl(`${environment.notificationWsUrl}?access_token=${accessToken}`)
  //       .withAutomaticReconnect()
  //       .build();
  //     this.hubConnection.on('ReceiveNotification', () => {
  //       this.store.dispatch(fromActions.getNotificationsCounter());
  //     });
  //     const startConnection = async () => {
  //       try {
  //         await this.hubConnection.start();
  //       } catch (err) {
  //         setTimeout(startConnection, 5000);
  //       }
  //     };
  //     this.hubConnection.onclose(() => {
  //       startConnection();
  //     });
  //     startConnection();
  //   }),
  // ), { dispatch: false });

  constructor(
    private actions$: Actions,
    private store: Store,
    private notificationService: NotificationService,
    private pushNotificationService: PushNotificationService,
    private pushNotificationOrganizationService: PushNotificationOrganizationService,
    private organizationVehicleService: OrganizationVehicleService,
  ) {
    this.store.select(selectUser).subscribe(user => {
      if (!user?.user_metadata) {
        return;
      }
      this.language = user?.user_metadata?.language || 'en';
      this.isCustomer = user?.roles?.some(
        (role: UserRole) => role === UserRole.Customer,
      );
    });
  }
}
